import React, {useContext, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {Alert, Button, Card, Col, Container, Form, Row, Spinner,} from "react-bootstrap";
import {toast} from "react-toastify";
import {format} from "date-fns";
import DatePicker from "react-datepicker";

import useClaims from "../Hooks/useClaims";
import useCompanyId from "../Hooks/useCompanyId";
import ActionModal from "../components/Modals/ActionModals/ActionModal";
import emitter from "eventBus";
import {SalvoContext} from "../contexts/SalvoContext";
import {useNavigate} from "react-router-dom";

import {convertTrialToPaid, extendTrialPeriod, getTrialInfo,} from "../API/trialApi";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCalendarXmark, faCoins, faRotate, faUser} from "@fortawesome/free-solid-svg-icons";
import ConditionalRender from "../components/ConditionalRender";
import useBalance from "../Hooks/useBalance";

const TrialPage = () => {
  const { t } = useTranslation();
  const companyId = useCompanyId();
  const { hasClaim } = useClaims();
  const { balance, loading: balanceLoading } = useBalance()


  const [loading, setLoading] = useState(false);
  const [creditsToAdd, setCreditsToAdd] = useState(0);
  const [newExpiry, setNewExpiry] = useState("");
  const [initialExpiry, setInitialExpiry] = useState("");
  const [updating, setUpdating] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const { trialInfo:trial, setTrialInfo } = useContext(SalvoContext);
  const navigate = useNavigate();

  useEffect(() => {
    if (trial) {
      setNewExpiry(new Date(trial.expiryDate));
      setInitialExpiry(new Date(trial.expiryDate));
    } else {
      // Reset if no trial
      setCreditsToAdd(0);
      setNewExpiry("");
    }
  }, [trial]);

  const loadTrialInfo = async () => {
    try {
      setLoading(true);
      const res = await getTrialInfo(companyId);
      setTrialInfo(res?.data.trial || null);
      setNewExpiry(trial?.expiryDate ? new Date(trial.expiryDate) : "");
      setCreditsToAdd(0);
    } catch (e) {
      toast.error(t("trial.fetchError"));
    } finally {
      setLoading(false);
    }
  };

  const handleConvert = async () => {
    try {
      setUpdating(true);
      await convertTrialToPaid(companyId);
      toast.success(t("trial.convertSuccess"));
      setTrialInfo(null);
      navigate("/billing");
    } catch (err) {
      toast.error(t("trial.convertError"));
    } finally {
      setUpdating(false);
    }
  };

  const handleExtend = async () => {
    try {
      setUpdating(true);
      await extendTrialPeriod(companyId, { creditsToAdd: creditsToAdd > 0 ? creditsToAdd : null, newExpiry });
      toast.success(t("trial.extendSuccess"));
      emitter.emit("refresh-balance");
      await loadTrialInfo();
    } catch (err) {
      toast.error(t("trial.extendError"));
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <Container className="mt-4 text-center">
        <Spinner animation="border" />
      </Container>
    );
  }

  if (!trial) {
    return (
      <Container className="mt-4">
        <h4>{t("trial.noActiveTrial")}</h4>
      </Container>
    );
  }

  const toLocalDatetimeInput = (isoString) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    const tzOffset = date.getTimezoneOffset() * 60000; // offset in ms
    const localISOTime = new Date(date.getTime() - tzOffset)
      .toISOString()
      .slice(0, 16); // "YYYY-MM-DDTHH:mm"
    return localISOTime;
  };

  return (
    <Container className="mt-4">
      <Card>
        <Card.Body>
          <h4>{t("trial.trialInformation")}</h4>
          <Row className="mt-3">
            <Col lg={4}>
              <Alert variant='light'>
                <FontAwesomeIcon icon={faCoins} />
                <span className='ms-2'>{t("trial.remainingCredits")}:</span>
                <ConditionalRender condition={balanceLoading}>
                  <Spinner size={'sm'} className={'ms-2'}/>
                </ConditionalRender>
                <span className={'ms-2 fw-bold'}>{balance}</span>
              </Alert>
            </Col>
            <Col lg={4}>
              <Alert variant='light'>
                <FontAwesomeIcon icon={faUser} />
                <span className='ms-2'>{t("trial.maxMsisdns")}:</span>
                <span className={'ms-2 fw-bold'}>{trial.maxMsisdns}</span>
              </Alert>
            </Col>
            <Col lg={4}>
              <Alert variant='light'>
                <FontAwesomeIcon icon={faCalendarXmark} />
                <span className='ms-2'>{t("trial.expiryDate")}:</span>
                <span className={'ms-2 fw-bold'}>{format(new Date(trial.expiryDate), "yyyy-MM-dd")}</span>
              </Alert>
            </Col>
          </Row>

          {trial.suspended && (
            <div className="text-danger mt-3">{t("trial.trialSuspended")}</div>
          )}

          {hasClaim("manage:trial") && (
            <div className="mt-4 d-flex justify-content-end">
              <Button
                variant="warning"
                onClick={() => setShowConfirm(true)}
                disabled={updating}
              >
                <FontAwesomeIcon icon={faRotate} className={'me-2'}/>
                {t("trial.convertToPaid")}
              </Button>
            </div>
          )}

          <ConditionalRender condition={hasClaim("extend:trial")}>
            <div className="mt-4">
              <hr/>
              <h5>{t("trial.extendTrial")}</h5>
              <Form>
               <Row>
                 <Col>
                   <Form.Group className="mb-3">
                     <Form.Label>{t("trial.addCredits")}</Form.Label>
                     <Form.Control
                         type="number"
                         value={creditsToAdd}
                         placeholder="0"
                         onChange={(e) => {
                           const value = e.target.value;
                           setCreditsToAdd(value === "" ? "" : Number(value));
                         }}
                         className="mb-3"
                     />
                   </Form.Group>
                 </Col>
                 <Col>
                   <Form.Group className="mb-3">
                     <Form.Label>{t("trial.newExpiryDate")}</Form.Label>
                     <div>
                       <DatePicker
                           selected={newExpiry}
                           onChange={(date) => setNewExpiry(date)}
                           dateFormat="dd-MM-yyyy"
                           className="form-control"
                           minDate={initialExpiry}
                           placeholderText="Select trial end date"
                       />
                     </div>
                   </Form.Group>
                 </Col>
               </Row>
                <Row>
                  <Col>
                    <Button
                        variant="success"
                        onClick={handleExtend}
                        disabled={updating}
                    >
                      {t("trial.submitExtension")}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </div>
          </ConditionalRender>
        </Card.Body>
      </Card>
      <ActionModal
          func={async () => {
            handleConvert();
            setShowConfirm(false);
          }}
          active={showConfirm}
          onCancel={async () => {
            setShowConfirm(false);
          }}
          positiveButton={{
            variant: "success",
            text: t("dialogs.yes") || "Ja",
          }}
          negativeButtonText={t("dialogs.no") || "Nee"}
          title={t("dialogs.confirmWorkdayToggle.title") || "Bevestiging"}
      >
        <span>{t("trial.confirmConvert")}</span>
      </ActionModal>
    </Container>
  );
};

export default TrialPage;
